import { Sparkles } from 'lucide-react'
import { cn } from '@insightai/ui'
import type { AISource } from './types'

export interface AIBadgeProps {
  source: AISource
  /** Warning treatment for below-threshold confidence (BB-FR-002). */
  tone?: 'default' | 'warning'
  showModel?: boolean
  className?: string
}

/**
 * F0-23 — the visible marker that content is AI-authored.
 *
 * `--ai-generated` is reserved for exactly this treatment (§4.1 rule 1), and the
 * reservation is enforced by `pnpm tokens:ai-guard`, which fails the build if a
 * decorative surface elsewhere reaches for the violet ramp.
 *
 * The model and version are rendered as text rather than hidden in a tooltip:
 * AG-FR-001 traceability is only useful if a Research Lead reviewing a report
 * can see which model produced a claim without hovering.
 */
export function AIBadge({ source, tone = 'default', showModel = true, className }: AIBadgeProps) {
  return (
    <span
      data-generation-id={source.generationId}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-caption font-medium',
        tone === 'default'
          ? 'border-ai-generated-border bg-ai-generated-surface text-ai-generated'
          : 'border-state-warning-border bg-state-warning-surface text-state-warning',
        className,
      )}
    >
      <Sparkles aria-hidden className="size-3" />
      <span>AI-generated</span>
      {showModel && (
        <span className="font-normal opacity-80">
          · {source.model} v{source.version}
        </span>
      )}
    </span>
  )
}
